import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react'

import { useSiteSettings } from '@/lib/useSiteSettings'
import { AppWrap, MotionWrap } from '@/wrapper'
import './Footer.scss'

type Status = 'idle' | 'sending' | 'sent' | 'error'

const INITIAL = { name: '', email: '', message: '', website: '' }

const Footer = () => {
  const { contactNote, calUrl } = useSiteSettings()
  const [formData, setFormData] = useState(INITIAL)
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const mountedAt = useRef(0)

  useEffect(() => {
    mountedAt.current = Date.now()
  }, [])

  const { name, email, message, website } = formData

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name: field, value } = e.target
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setStatus('sending')
    setError(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message,
          website,
          elapsedMs: Date.now() - mountedAt.current,
        }),
      })

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(body?.error ?? 'Something went wrong. Please try again.')
      }

      setFormData(INITIAL)
      setStatus('sent')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  const isSending = status === 'sending'

  return (
    <>
      <h2 className='head-title head-text'>
        Tell me what you&apos;re <span>building</span>
      </h2>

      {/* The address and phone number used to sit here as cards. They repeated
          the form standing next to them and published two identifiers that
          cannot be unpublished once harvested. The number is in the CV, which
          is where someone who genuinely wants to call will already be. */}
      {contactNote && <p className='app__footer-note p-text'>{contactNote}</p>}

      {status !== 'sent' ? (
        <form className='app__footer-form app__flex' onSubmit={handleSubmit} noValidate={false}>
          <div className='app__flex'>
            <label className='sr-only' htmlFor='contact-name'>
              Your Name
            </label>
            <input
              id='contact-name'
              className='p-text'
              type='text'
              name='name'
              placeholder='Your Name'
              value={name}
              onChange={handleChangeInput}
              required
              minLength={2}
              maxLength={80}
              autoComplete='name'
            />
          </div>
          <div className='app__flex'>
            <label className='sr-only' htmlFor='contact-email'>
              Your Email
            </label>
            <input
              id='contact-email'
              className='p-text'
              type='email'
              name='email'
              placeholder='Your Email'
              value={email}
              onChange={handleChangeInput}
              required
              autoComplete='email'
            />
          </div>
          <div>
            <label className='sr-only' htmlFor='contact-message'>
              Your Message
            </label>
            <textarea
              id='contact-message'
              className='p-text'
              placeholder='Your Message'
              value={message}
              name='message'
              onChange={handleChangeInput}
              required
              minLength={10}
              maxLength={2000}
            />
          </div>

          {/* Honeypot — hidden from humans, irresistible to bots. */}
          <div className='app__footer-honeypot' aria-hidden='true'>
            <label htmlFor='contact-website'>Leave this field empty</label>
            <input
              id='contact-website'
              type='text'
              name='website'
              value={website}
              onChange={handleChangeInput}
              tabIndex={-1}
              autoComplete='off'
            />
          </div>

          <div className='app__footer-actions'>
            <button type='submit' className='p-text' disabled={isSending}>
              {isSending ? 'Sending…' : 'Send message'}
            </button>

            {/* Secondary on purpose: some people would rather talk than write,
                but offering a calendar first invites a meeting before there is
                anything to meet about. */}
            {calUrl && (
              <a className='app__footer-book' href={calUrl} target='_blank' rel='noreferrer'>
                Or book 20 minutes
              </a>
            )}
          </div>

          <p className='app__footer-status p-text' role='status' aria-live='polite'>
            {error}
          </p>
        </form>
      ) : (
        <div role='status' aria-live='polite'>
          <h3 className='head-text'>Thank you for getting in touch!</h3>
          <p className='p-text' style={{ textAlign: 'center', marginTop: '0.5rem' }}>
            I&apos;ll get back to you shortly.
          </p>
        </div>
      )}
    </>
  )
}

export default AppWrap(MotionWrap(Footer, 'app__footer'), 'contact', 'app__whitebg')
